package com.evoapps.evoshift.domain.timeline

import com.evoapps.evoshift.domain.market.payroll.PayrollFact
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.model.StaffChecks
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.repository.EmployeeTimelineRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.StaffRepository
import com.evoapps.evoshift.domain.repository.TimelineDoc
import com.evoapps.evoshift.domain.repository.TimelineWriteBatch
import com.evoapps.evoshift.domain.repository.UnpaidWorkWrite
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.finance.RepriceFutureShiftsUseCase
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.first
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlin.random.Random
import kotlin.time.Instant

/** SH-212: what was done to an entry, for the audit trail. */
enum class TimelineAuditAction { ADDED, MOVED, CHANGED, REMOVED }

/**
 * SH-212: one change in the audit trail (users/{uid}/timeline_audit/{wpId}): when ([at], an ISO
 * instant), who ([by], [byName] as they were called then), what (the [kind] of entry, [fact] for
 * a tax relief) and its dates before and after ("YYYY-MM-DD"; null: none / from the start).
 * Dates only, never amounts: the trail says what was changed, the pay terms say to what.
 */
data class TimelineAuditEntry(
    val at: String,
    val by: String,
    val byName: String,
    val action: TimelineAuditAction,
    val kind: TimelineKind,
    val fact: PayrollFact? = null,
    val from: String? = null,
    val until: String? = null,
    val previousFrom: String? = null,
    val previousUntil: String? = null
)

object TimelineAudit {
    /** Entries kept (the oldest go first); firestore.rules allow as many. */
    const val MAX_ENTRIES = 200

    /** The trail entry for [edit] of [before] (the entry it replaced or removed, when one). */
    fun entryFor(edit: TimelineEdit, before: EmployeeTimeline, at: Instant, editor: User): TimelineAuditEntry {
        val old = when (edit) {
            is TimelineEdit.Replace -> before.entry(edit.id)
            is TimelineEdit.Remove -> before.entry(edit.id)
            is TimelineEdit.Add -> null
        }
        val new = when (edit) {
            is TimelineEdit.Add -> edit.entry
            is TimelineEdit.Replace -> edit.entry
            is TimelineEdit.Remove -> null
        }
        val action = when (edit) {
            is TimelineEdit.Add -> TimelineAuditAction.ADDED
            is TimelineEdit.Remove -> TimelineAuditAction.REMOVED
            is TimelineEdit.Replace -> if (old != null && new != null && old.sameValuesAs(new)) TimelineAuditAction.MOVED else TimelineAuditAction.CHANGED
        }
        val subject = new ?: old
        return TimelineAuditEntry(
            at = at.toString(),
            by = editor.uid,
            byName = editor.displayName,
            action = action,
            kind = subject?.kind ?: TimelineKind.NOTE,
            fact = subject?.fact,
            from = new?.from?.toString(),
            until = new?.until?.toString(),
            previousFrom = old?.from?.toString(),
            previousUntil = old?.until?.toString()
        )
    }
}

/** What the timeline screen shows. */
data class TimelineView(
    val timeline: EmployeeTimeline,
    val personName: String,
    /** The signed-in person's own timeline (read-only for them). */
    val isMine: Boolean,
    /** Owners and financial managers while the plan runs (an owner also their own). */
    val canEdit: Boolean,
    /** Owners and financial managers: it names who changed what. */
    val audit: List<TimelineAuditEntry> = emptyList(),
    val shifts: ShiftDays = ShiftDays.NONE,
    val currency: String = "PLN",
    /** SH-224: the person's pay terms here (their contract on a day decides the request types offered); null: none or unknown. */
    val financial: com.evoapps.evoshift.domain.model.FinancialEngagement? = null,
    /** SH-224: the workplace's request types and limits, and its market (the limits' rules). */
    val requestSettings: com.evoapps.evoshift.domain.model.RequestSettings = com.evoapps.evoshift.domain.model.RequestSettings(),
    val market: com.evoapps.evoshift.domain.market.Market = com.evoapps.evoshift.domain.market.Market.POLAND,
    /** SH-224: the viewer approves and rejects requests here (an editor who decides requests, SH-223). */
    val canDecideRequests: Boolean = false
)

/** A saved edit: the timeline as it is now, and what it reprices (null: nothing). */
data class TimelineSaved(val view: TimelineView, val recompute: RecomputeTrigger?)

/**
 * SH-269: edits of one person's timeline ready to be written ([write]), with what they leave
 * ([saved]), the timeline before them ([before]) and the edits as applied ([applied], notes with
 * their ids), from which a bulk change builds its Undo.
 */
data class PreparedTimelineWrite(
    val write: TimelineWriteBatch,
    val saved: TimelineSaved,
    val before: EmployeeTimeline,
    val applied: List<TimelineEdit>
) {
    /** No edit applied: nothing to write. */
    val isEmpty: Boolean get() = applied.isEmpty()
}

/** The workplace's own today, from its time zone (the device's when unknown). */
internal fun Workplace.today(): LocalDate {
    val zone = runCatching { TimeZone.of(settings.timezone) }.getOrElse { TimeZone.currentSystemDefault() }
    return AppClock.todayIn(zone)
}

/**
 * SH-212: reads a person's timeline at a workplace and writes edits back. Owners and financial
 * managers (MANAGE_PAY, as the pay terms and HR dates) read anyone's and edit; everybody reads
 * their own, without the audit trail. firestore.rules enforce the same.
 */
class EmployeeTimelineUseCase(
    private val timelines: EmployeeTimelineRepository,
    private val users: UserRepository,
    private val staff: StaffRepository,
    private val shiftRepository: ShiftRepository,
    private val rosters: RosterRepository,
    private val workplaces: WorkplaceRepository,
    private val reprice: RepriceFutureShiftsUseCase? = null,
    private val today: ((Workplace) -> LocalDate)? = null,
    private val now: () -> Instant = { AppClock.now() },
    private val newId: () -> String = { "n" + Random.nextLong().toULong().toString(36) },
    /** SH-232: who counts in the labour cost and SPMH (SH-206); without it the lane is not shown. */
    private val labourInclusion: com.evoapps.evoshift.domain.repository.LabourInclusionRepository? = null
) {
    private class Loaded(val view: TimelineView, val sources: TimelineSources, val doc: TimelineDoc, val workplace: Workplace)

    /** [userId]'s timeline at [workplaceId] as [viewer] may see it. Fails for anyone else. */
    suspend fun load(workplaceId: String, userId: String, viewer: User, planActive: Boolean = true): Result<TimelineView> =
        runCatching { read(workplaceId, userId, viewer, planActive).view }

    private suspend fun read(workplaceId: String, userId: String, viewer: User, planActive: Boolean): Loaded = coroutineScope {
        val mine = userId == viewer.uid
        val capabilities = viewer.capabilitiesAt(workplaceId, planActive)
        val manages = Capability.MANAGE_PAY in capabilities
        if (!mine && !manages) throw IllegalStateException("Only owners and financial managers read someone else's timeline")
        val workplace = async { workplaces.getWorkplace(workplaceId).getOrThrow() }
        val profile = async { if (mine) viewer else users.getUserProfile(userId).getOrThrow() }
        val terms = async {
            val sensitive = users.getSensitiveUserData(userId)
            if (sensitive.exceptionOrNull() is NotFoundException) null else sensitive.getOrNull()?.financialEngagements?.get(workplaceId)
        }
        val record = async { staff.getStaffRecord(userId, workplaceId).getOrNull() }
        val doc = async { timelines.getTimelineDoc(userId, workplaceId).getOrDefault(TimelineDoc()) }
        val audit = async { if (manages) timelines.getAudit(userId, workplaceId).getOrDefault(emptyList()) else emptyList() }
        // SH-232: the labour inclusion setting, for whoever sees the workplace's finances (as the rules allow).
        val labourPeriods = async {
            if (Capability.VIEW_FINANCES in capabilities) labourInclusion?.get(workplaceId, userId)?.getOrNull() else null
        }
        val shifts = async { if (manages) shiftDays(workplaceId, userId) else ShiftDays.NONE }
        val wp = workplace.await()
        val person = profile.await()
        val day = today?.invoke(wp) ?: wp.today()
        val financial = terms.await()
        // SH-232: working without pay: everyone who sees pay here, the person their own when they have some; never
        // the workplace's owner (whose unpaid shifts are the workplace setting's).
        val unpaidPeriods = wp.unpaidPeriodsOf(userId)
        val unpaid = unpaidPeriods.takeIf { userId != wp.ownerId && (manages || (mine && it.isNotEmpty())) }
        val labour = labourPeriods.await()?.let { periods ->
            LabourSource(
                periods = periods,
                hasPayTerms = financial != null,
                isOwnerLevel = userId == wp.ownerId || person.engagements[workplaceId]?.permission == UserPermission.OWNER,
                ownerShiftsUnpaid = wp.settings.payroll.ownerShiftsUnpaid && userId == wp.ownerId,
                unpaid = unpaidPeriods
            )
        }
        val sources = TimelineSources(
            person.engagements[workplaceId], financial, record.await(), doc.await().notes, unpaid, labour,
            plannedEndOn = day(doc.await().plannedEndOn)
        )
        val reliefs = PayrollRules.of(wp.settings.market).taxReliefs
        val canEdit = manages && Capability.MAKE_CHANGES in capabilities &&
            (!mine || viewer.engagements[workplaceId]?.permission == UserPermission.OWNER)
        val view = TimelineView(
            timeline = TimelineReader.read(userId, workplaceId, sources, day, reliefs),
            personName = person.displayName,
            isMine = mine,
            canEdit = canEdit,
            audit = audit.await(),
            shifts = shifts.await(),
            currency = wp.settings.currency,
            financial = sources.financial,
            requestSettings = wp.settings.requests,
            market = wp.settings.market,
            canDecideRequests = canEdit && Capability.APPROVE_REQUESTS in capabilities
        )
        Loaded(view, sources, doc.await(), wp)
    }

    /**
     * The first and last day of [userId]'s shifts here, over the workplace's months. SH-237: the
     * shifts they actually worked (or work), as pay and hours count them ([workerId][com.evoapps.evoshift.domain.model.workerId]):
     * one a colleague covered is the colleague's, one nobody came to is nobody's, and each part of
     * a split is its own person's.
     */
    private suspend fun shiftDays(workplaceId: String, userId: String): ShiftDays {
        val months = rosters.observeRosters(workplaceId).first()
        if (months.isEmpty()) return ShiftDays.NONE
        val first = months.minOf { it.year * 12 + it.month - 1 }
        val last = months.maxOf { it.year * 12 + it.month - 1 }
        fun iso(index: Int, end: Boolean): String {
            val y = index / 12
            val m = index % 12 + 1
            return "$y-${m.toString().padStart(2, '0')}-${if (end) "31" else "01"}"
        }
        val theirs = shiftRepository.getShiftsWorkedInWorkplace(workplaceId, userId, iso(first, false), iso(last, true)).getOrThrow()
        val days = theirs.mapNotNull { day(it.date.ifEmpty { it.plannedTime.start }) }
        return ShiftDays(days.minOrNull(), days.maxOrNull())
    }

    /**
     * Applies [edit] to [userId]'s timeline as [editor] (owner or financial manager), checks it
     * ([InvalidTimelineException]), writes it back to its sources with the audit entry in one
     * batch and says what it reprices. The repricing itself is [recompute]'s, run after.
     */
    suspend fun save(workplaceId: String, userId: String, edit: TimelineEdit, editor: User): Result<TimelineSaved> = runCatching {
        val prepared = prepare(workplaceId, userId, editor) { listOf(edit) }.getOrThrow()
        timelines.save(prepared.write).getOrThrow()
        prepared.saved
    }

    /**
     * SH-269: the edits [edits] gives for [userId]'s current timeline, applied one after the other
     * as [save] applies one (each checked, each with its audit entry), ready to be written with
     * others ([EmployeeTimelineRepository.saveAll]: a bulk change writes a workplace's people in one
     * batch). Nothing is written here. No edits: a write that changes nothing but the audit is not
     * made ([PreparedTimelineWrite.isEmpty]).
     */
    suspend fun prepare(
        workplaceId: String,
        userId: String,
        editor: User,
        edits: (EmployeeTimeline) -> List<TimelineEdit>
    ): Result<PreparedTimelineWrite> = runCatching {
        val loaded = read(workplaceId, userId, editor, planActive = true)
        if (!loaded.view.canEdit) throw IllegalStateException("Only owners and financial managers change a timeline")
        val start = loaded.view.timeline
        val day = start.today
        var current = start
        var removesHire = false
        var audit = loaded.view.audit
        val applied = mutableListOf<TimelineEdit>()
        for (edit in edits(start)) {
            val stamped = when (edit) {
                is TimelineEdit.Add -> if (edit.entry.kind == TimelineKind.NOTE && edit.entry.noteId == null) edit.copy(entry = edit.entry.copy(noteId = newId())) else edit
                is TimelineEdit.Replace -> if (edit.entry.kind == TimelineKind.NOTE) edit.copy(entry = edit.entry.copy(noteId = current.entry(edit.id)?.noteId)) else edit
                is TimelineEdit.Remove -> edit
            }
            val removes = stamped is TimelineEdit.Remove && stamped.id == TimelineKind.HIRE.name
            // Removing the hire takes the person off the workplace (as "Remove" on the employee form):
            // only someone who never had a shift here; the hire stays as the start of what is ended today.
            val entries = if (removes) {
                current.entries.filterNot { it.kind == TimelineKind.END } + TimelineEntry(TimelineKind.END, day)
            } else {
                TimelineRules.apply(current, stamped)
            }
            val next = current.copy(entries = entries)
            // SH-234: only what this edit brings in; older inconsistencies never block an unrelated change.
            val problems = TimelineRules.refusals(current, next, loaded.view.shifts, removedHire = removes)
            if (problems.isNotEmpty()) throw InvalidTimelineException(problems)
            audit = audit + TimelineAudit.entryFor(stamped, current, now(), editor)
            removesHire = removesHire || removes
            applied += stamped
            current = next
        }
        val entries = current.entries
        val reliefs = start.reliefs
        val written = TimelineWriter.write(userId, workplaceId, loaded.sources, entries, day, reliefs).let {
            if (removesHire) it.copy(engagement = EngagementDates(loaded.sources.engagement?.startDate.orEmpty(), day.toString(), isActive = false), plannedEndOn = null) else it
        }
        val doc = TimelineDoc(written.notes ?: loaded.doc.notes, written.plannedEndOn?.toString())
        val docChanged = doc != loaded.doc
        audit = audit.takeLast(TimelineAudit.MAX_ENTRIES)
        val readiness = written.staff?.let { StaffChecks.statusOnly(StaffChecks.readiness(it, userId, workplaceId, day)) }
        // SH-232: everyone's periods without pay with this person's changed, and the older list as of today.
        val unpaidWrite = written.unpaid?.let { periods ->
            val all = com.evoapps.evoshift.domain.budget.UnpaidWork.withPeriods(loaded.workplace.settings.payroll, userId, periods)
            UnpaidWorkWrite(all, com.evoapps.evoshift.domain.budget.UnpaidWork.legacyOn(all, emptyList(), day.toString()), day.toString())
        }
        val write = TimelineWriteBatch(
            userId = userId,
            workplaceId = workplaceId,
            editorId = editor.uid,
            engagement = written.engagement,
            financial = written.financial,
            staff = written.staff?.copy(updatedAt = now().toString(), updatedBy = editor.uid),
            readiness = readiness,
            timelineDoc = doc.takeIf { docChanged },
            audit = audit,
            unpaid = unpaidWrite,
            labourInclusion = written.labour
        )

        val engagement = loaded.sources.engagement?.let { e ->
            written.engagement?.let { e.copy(startDate = it.startDate, endDate = it.endDate, isActive = it.isActive) } ?: e
        }
        val unpaidNow = written.unpaid ?: loaded.sources.unpaid
        val sources = TimelineSources(
            engagement, written.financial ?: loaded.sources.financial, written.staff ?: loaded.sources.staff, doc.notes,
            unpaid = unpaidNow,
            labour = loaded.sources.labour?.let { l -> l.copy(periods = written.labour ?: l.periods, unpaid = written.unpaid ?: l.unpaid) },
            plannedEndOn = day(doc.plannedEndOn)
        )
        val now = TimelineReader.read(userId, workplaceId, sources, day, reliefs)
        PreparedTimelineWrite(
            write = write,
            saved = TimelineSaved(loaded.view.copy(timeline = now, audit = audit), TimelineRules.recompute(start, now)),
            before = start,
            applied = applied
        )
    }

    /**
     * Reprices what [trigger] says, in the open months only (RepriceFutureShiftsUseCase skips
     * LOCKED and ARCHIVED ones, whose snapshots are history): from its day, or for a change "from
     * the start" from the hire (every open month when unknown). Returns how many shifts were priced.
     */
    suspend fun recompute(workplaceId: String, trigger: RecomputeTrigger, hire: LocalDate?): Result<Int> {
        val use = reprice ?: return Result.success(0)
        return use(workplaceId, trigger.from ?: hire ?: EARLIEST)
    }

    private companion object {
        val EARLIEST = LocalDate(2000, 1, 1)
    }
}
